import React from "react";
import { Container } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

import CustomNavbar from "./CustomNavbar";
import Footer from "./Footer";

/**
 * Chrome for the admin screens. Both previously rendered as a bare
 * <Container> with no navbar or footer, so they read as a different site.
 */
function AdminShell({ title, subtitle, icon, actions, maxWidth = "1000px", children }) {
  return (
    <>
      <CustomNavbar />

      <style>{`
        .adm {
          --peach-deep: #ff6b35;
          --peach: #ff9066;
          --peach-soft: #f3c09f;
          --peach-tint: #ffe9e0;
          --peach-ink: #c2410c;
        }
        .adm-head {
          position: relative; overflow: hidden;
          padding: clamp(34px, 4.5vw, 56px) 0;
          color: #fff;
          background:
            radial-gradient(34rem 18rem at 86% 4%, rgba(255,107,53,.30), transparent 62%),
            linear-gradient(150deg, #131f36 0%, #0f172a 55%, #0b1120 100%);
        }
        .adm-head__mesh {
          position: absolute; inset: 0; pointer-events: none;
          background-image:
            linear-gradient(rgba(255,144,102,.07) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,144,102,.07) 1px, transparent 1px);
          background-size: 64px 64px;
          mask-image: radial-gradient(70% 60% at 40% 45%, #000 20%, transparent 100%);
          -webkit-mask-image: radial-gradient(70% 60% at 40% 45%, #000 20%, transparent 100%);
        }
        .adm-back {
          display: inline-flex; align-items: center; gap: 8px;
          color: var(--peach-soft); text-decoration: none;
          font-weight: 600; font-size: .86rem;
          transition: color .2s ease, transform .2s ease;
        }
        .adm-back:hover, .adm-back:focus-visible { color: var(--peach); transform: translateX(-3px); }
        .adm-bar {
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 16px; margin-top: 18px;
        }
        .adm-title { display: flex; align-items: center; gap: 16px; }
        .adm-title__icon {
          width: 58px; height: 58px; border-radius: 17px; flex-shrink: 0;
          display: grid; place-items: center; font-size: 1.25rem; color: #fff;
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          box-shadow: 0 12px 26px rgba(255,107,53,.4);
        }
        .adm-title h1 {
          margin: 0; font-weight: 800; letter-spacing: -.02em; color: #fff;
          font-size: clamp(1.4rem, 3vw, 2rem);
        }
        .adm-title p { margin: 4px 0 0; color: #cbd5e1; font-size: .9rem; }

        /* Bottom padding leaves room for the footer's overlapping CTA panel */
        .adm-body { padding: clamp(30px, 4vw, 48px) 0 clamp(92px, 10vw, 130px); background: #fff; }
        .adm-panel {
          background: #fff; border-radius: 20px;
          border: 1px solid #f0e4dd;
          box-shadow: 0 10px 30px rgba(194,65,12,.1);
          overflow: hidden;
        }
        .adm-btn {
          display: inline-flex; align-items: center; gap: 8px;
          border-radius: 12px; font-weight: 700; font-size: .88rem;
          padding: 10px 18px; border: none; text-decoration: none;
          transition: transform .2s ease, box-shadow .2s ease,
                      background .2s ease, color .2s ease;
        }
        .adm-btn--solid {
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          color: #0f172a; box-shadow: 0 10px 22px rgba(255,107,53,.34);
        }
        .adm-btn--solid:hover:not(:disabled) {
          color: #fff; transform: translateY(-2px);
          box-shadow: 0 16px 30px rgba(255,107,53,.46);
        }
        .adm-btn--ghost {
          background: rgba(255,255,255,.07); color: #e2e8f0;
          border: 1px solid rgba(255,144,102,.3);
        }
        .adm-btn--ghost:hover, .adm-btn--ghost:focus-visible {
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          border-color: transparent; color: #0f172a; transform: translateY(-2px);
        }
        .adm-btn:disabled { opacity: .6; cursor: not-allowed; }

        .adm-form .form-label {
          font-weight: 600; font-size: .85rem; color: #334155; margin-bottom: 6px;
        }
        .adm-form .form-control {
          border: 1px solid #e6dbd4; border-radius: 12px;
          padding: 11px 14px; font-size: .94rem; background: #fffdfc;
          transition: border-color .2s ease, box-shadow .2s ease;
        }
        .adm-form .form-control:focus {
          border-color: var(--peach);
          box-shadow: 0 0 0 4px rgba(255,144,102,.22);
        }
        .adm-hint { color: #94a3b8; font-size: .78rem; margin-top: 5px; }

        @media (prefers-reduced-motion: reduce) {
          .adm-back, .adm-btn { transition: none; }
          .adm-back:hover, .adm-btn:hover { transform: none; }
        }
      `}</style>

      <div className="adm">
        <section className="adm-head">
          <span className="adm-head__mesh" />
          <Container style={{ maxWidth, position: "relative", zIndex: 1 }}>
            <Link to="/account" className="adm-back">
              <FaArrowLeft />
              Back to Account
            </Link>

            <div className="adm-bar">
              <div className="adm-title">
                <span className="adm-title__icon">{icon}</span>
                <div>
                  <h1>{title}</h1>
                  {subtitle && <p>{subtitle}</p>}
                </div>
              </div>

              {actions}
            </div>
          </Container>
        </section>

        <section className="adm-body">
          <Container style={{ maxWidth, minHeight: "40vh" }}>{children}</Container>
        </section>
      </div>

      <Footer />
    </>
  );
}

export default AdminShell;
